import { useEffect, useMemo, useState } from "react";
import { api, INR, INR2 } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { TrendingUp, TrendingDown, Search, ShieldCheck } from "lucide-react";
import BuyEtfModal from "@/components/modals/BuyEtfModal";

const SORTS = [
  { v: "aum",    label: "AUM" },
  { v: "ltp",    label: "Price" },
  { v: "chg",    label: "Change" },
  { v: "exp",    label: "Expense" },
  { v: "ret_1y", label: "1Y Ret" },
];

export default function EtfList({ onPortfolioChange }) {
  const [etfs, setEtfs] = useState([]);
  const [portfolio, setPortfolio] = useState({ holdings: [], total_invested_inr: 0, total_current_inr: 0, total_pnl_pct: 0 });
  const [sort, setSort] = useState("aum");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    const [l, p] = await Promise.all([
      api.get(`/etfs?sort=${sort}&order=desc`),
      api.get("/etfs/portfolio/me"),
    ]);
    setEtfs(l.data.etfs || []);
    setPortfolio(p.data);
    onPortfolioChange && onPortfolioChange(p.data);
  };

  useEffect(() => { refresh(); }, [sort]);

  const filtered = useMemo(() => {
    if (!q) return etfs;
    const needle = q.toLowerCase();
    return etfs.filter(
      (e) =>
        e.name.toLowerCase().includes(needle) ||
        e.symbol.toLowerCase().includes(needle) ||
        e.amc.toLowerCase().includes(needle)
    );
  }, [etfs, q]);

  const handleBuy = (etf) => { setSelected(etf); setOpen(true); };

  return (
    <div>
      {/* Portfolio summary */}
      {portfolio.holdings.length > 0 && (
        <div data-testid="etf-portfolio-card" className="mb-4 rounded-3xl bg-brand-sand border border-brand-line p-5 shadow-soft">
          <div className="text-xs uppercase tracking-[0.2em] text-brand-primary font-bold">Your ETF portfolio</div>
          <div className="mt-2 flex items-baseline justify-between">
            <div>
              <div className="font-display text-3xl text-brand-cream tabular-nums">
                {INR(portfolio.total_current_inr)}
              </div>
              <div className="text-xs text-brand-slate tabular-nums">
                Invested {INR(portfolio.total_invested_inr)}
              </div>
            </div>
            <div className={`rounded-full px-3 py-1 text-sm font-semibold ${portfolio.total_pnl_pct >= 0 ? "bg-emerald-900/30 text-brand-secondary" : "bg-red-900/30 text-brand-danger"}`}>
              {portfolio.total_pnl_pct >= 0 ? "+" : ""}{portfolio.total_pnl_pct}%
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            {portfolio.holdings.map((h) => (
              <div key={h.etf_id} className="flex items-center justify-between text-xs">
                <div className="flex-1 min-w-0">
                  <span className="font-semibold text-brand-cream">{h.symbol}</span>
                  <span className="text-brand-slate ml-1 truncate">· {h.units}u @ {INR2(h.avg_price)}</span>
                </div>
                <div className="text-right tabular-nums">
                  <div className="text-brand-cream">{INR(h.current_value_inr)}</div>
                  <div className={h.pnl_pct >= 0 ? "text-brand-secondary" : "text-brand-danger"}>
                    {h.pnl_pct >= 0 ? "+" : ""}{h.pnl_pct}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-slate2" />
        <Input
          data-testid="etf-search"
          placeholder="Search ETF, AMC or symbol"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 h-11 rounded-full bg-brand-sand2 border-brand-line-faint text-brand-cream placeholder:text-brand-slate2"
        />
      </div>

      {/* Sort chips */}
      <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
        {SORTS.map((s) => (
          <button
            key={s.v}
            data-testid={`etf-sort-${s.v}`}
            onClick={() => setSort(s.v)}
            className={`shrink-0 h-8 px-3 rounded-full text-xs font-semibold border transition-transform active:scale-95 ${
              sort === s.v
                ? "bg-brand-primary text-brand-page border-brand-primary"
                : "bg-brand-sand2 text-brand-cream border-brand-line-faint"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div data-testid="etf-list" className="mt-4 space-y-2.5">
        {filtered.length === 0 && (
          <div className="p-6 text-brand-slate text-sm text-center">No matching ETFs.</div>
        )}
        {filtered.map((e) => {
          const up = (e.chg_pct || 0) >= 0;
          return (
            <div
              key={e.id}
              data-testid={`etf-card-${e.id}`}
              className="rounded-2xl border border-brand-line-faint bg-brand-sand p-4 shadow-soft hover:border-brand-line transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 shrink-0 rounded-xl bg-gold-gradient text-brand-page font-display text-lg font-bold flex items-center justify-center shadow-gold">
                  {e.name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-brand-cream truncate">{e.name}</div>
                      <div className="text-[11px] text-brand-slate">
                        {e.amc} · <span className="text-brand-accent">{e.symbol}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display text-lg text-brand-cream tabular-nums leading-tight">
                        {INR2(e.ltp)}
                      </div>
                      <div className={`text-[10px] font-semibold flex items-center gap-1 justify-end ${up ? "text-brand-secondary" : "text-brand-danger"}`}>
                        {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />} {e.chg_pct}%
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                    <span className="rounded-full bg-brand-secondary/15 text-brand-secondary px-2 py-0.5 font-semibold flex items-center gap-1">
                      <ShieldCheck size={9} /> Govt-backed
                    </span>
                    <span className="text-brand-slate">AUM ₹{Number(e.aum_cr).toLocaleString("en-IN")} Cr</span>
                    <span className="text-brand-slate">· Exp {e.exp_ratio}%</span>
                    <span className="text-brand-slate">· 1Y {e.ret_1y}%</span>
                  </div>
                  <button
                    data-testid={`etf-buy-${e.id}`}
                    onClick={() => handleBuy(e)}
                    className="mt-3 h-9 w-full rounded-full bg-brand-primary/12 border border-brand-primary text-brand-primary text-sm font-semibold hover:bg-brand-primary hover:text-brand-page transition-colors"
                  >
                    Buy
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <BuyEtfModal open={open} onOpenChange={setOpen} etf={selected} onDone={refresh} />
    </div>
  );
}
